import { Text } from "@certn/cascadia";
import {
  parseSectionMessageType,
  RequirementsList,
  SectionMessage,
} from "@certn/cascadia-react";
import { parseCascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useContext, useMemo } from "react";
import Markdown from "react-markdown";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";
import {
  evaluateValidationRules,
  ValidationRuleGroup,
} from "./validationRulesUtils";

// -- Component --

function SectionMessageControl(props: CascadiaControlProps) {
  const options = props.uischema?.options || {};
  const {
    isSummaryForPath,
    isSubmitted,
    values: rootValues,
  } = useContext(CascadiaJsonFormContext);

  const validationRules = useMemo(
    () =>
      Array.isArray(options.validation_rules)
        ? (options.validation_rules as ValidationRuleGroup[])
        : undefined,
    [options.validation_rules]
  );

  const evaluatedGroups = useMemo(
    () =>
      validationRules?.length
        ? evaluateValidationRules(
            validationRules,
            rootValues as Record<string, unknown> | undefined,
            isSubmitted
          )
        : [],
    [validationRules, rootValues, isSubmitted]
  );

  const hasValidationRules = evaluatedGroups.length > 0;

  if (
    (!(options.showInSummary ?? true) && isSummaryForPath?.(props.path)) ||
    (!options?.title && !options?.body && !hasValidationRules)
  ) {
    return null;
  }

  const isPlain = options?.variant === "plain";
  const icon = parseCascadiaStandardIcon(options?.icon, "info");
  const type = parseSectionMessageType(options?.type, "info");
  const body = options?.useScopeAsBody === true ? props.data : options?.body;
  return (
    <SectionMessage
      type={type}
      sectionTitle={options?.title}
      icon={isPlain ? undefined : icon}
      noPadding={isPlain}
      testID={options?.testId}
      className={isPlain ? styles.plainMessage : undefined}
    >
      {body && options?.body_is_markdown ? (
        <Markdown
          components={{
            p: "span",
            ul(props) {
              const { node, ref, ...rest } = props;
              return <ul className={styles.messagesList} {...rest} />;
            },
            a(props) {
              const { node, ref, href, children, ...rest } = props;
              const isExternal = /^https?:\/\//i.test(href ?? "");
              return (
                <a
                  href={href}
                  {...(isExternal
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  {...rest}
                >
                  {children}
                </a>
              );
            },
          }}
        >
          {body}
        </Markdown>
      ) : body ? (
        <Text color="text-base">{body}</Text>
      ) : null}
      {hasValidationRules && (
        <div className={body ? styles.validationRulesWithBody : undefined}>
          {evaluatedGroups.map((group) => (
            <div
              key={group.label}
              className={
                isPlain
                  ? `${styles.validationGroup} ${styles.validationGroupPlain}`
                  : styles.validationGroup
              }
            >
              <Text
                color="text-base"
                style="form/section-heading"
                className={isPlain ? styles.plainGroupLabel : undefined}
              >
                {group.label}
              </Text>
              <RequirementsList
                requirementsList={group.items.map((item) => ({
                  ...item,
                  text: (
                    <Markdown
                      components={{
                        p: "span",
                        ul: ({ node, ref, ...rest }) => (
                          <ul className={styles.markdownSublist} {...rest} />
                        ),
                        li: ({ node, ref, ...rest }) => (
                          <li
                            className={styles.markdownSublistItem}
                            {...rest}
                          />
                        ),
                      }}
                    >
                      {item.text}
                    </Markdown>
                  ),
                }))}
                testID={`validation-rules-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
              />
            </div>
          ))}
        </div>
      )}
    </SectionMessage>
  );
}

const styles = {
  plainMessage: css({
    marginTop: "calc(-1 * var(--space-4))",
  }),
  messagesList: css({
    paddingInlineStart: 21,
  }),
  validationRulesWithBody: css({
    marginTop: 8,
  }),
  validationGroup: css({
    marginTop: 4,
  }),
  validationGroupPlain: css({
    marginBottom: 15,
  }),
  plainGroupLabel: css({
    fontSize: 14,
  }),
  markdownSublist: css({
    marginTop: "var(--space-05)",
    marginBottom: 0,
    marginLeft: "var(--space-2)",
    paddingLeft: "var(--space-2)",
    listStyleType: "disc",
  }),
  markdownSublistItem: css({
    marginTop: "var(--space-05)",
    "&::marker": {
      color: "var(--text-weak)",
    },
  }),
};

SectionMessageControl.displayName = "SectionMessageControl";

export const SectionMessageControlRenderer = withCascadiaControlProps(
  SectionMessageControl
);
